import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import LightElement from './light-element.js';
import { IFiltersResponse, IFilter, IFilterValue, IAppliedFilter } from '../../typescript/models/IFiltersResponse';
import { live } from 'lit/directives/live.js';
import Helpers from '../../typescript/modules/helpers.js';

declare var dataLayer: any;

@customElement('quick-filters')
export class QuickFilters extends LightElement {

	@property({ type: Array })
	filtersToShow: string[] = [];

	@property({ type: Array })
	appliedFiltersToHide: string[] = [];

	@property({ type: Boolean })
	showAllFiltersButton: boolean = false;

	@property({ type: Boolean })
	showClearAll: boolean = false;

	/**
	 * When enabled, displays a dedicated "Next day" quick filter option.
	 *
	 * Use this when the consumer wants to allow users to quickly filter results
	 * to items that fall on the day after the currently selected date or range.
	 *
	 * This flag does not implement filtering on its own; instead, the "Next day"
	 * control adds or updates the corresponding date filter within the main
	 * filter system (using the same filter keys and appliedFilters state), so
	 * the behavior remains consistent with other filters.
	 */
	@property({ type: Boolean })
	showNextDay: boolean = false;

	@property({ type: Object })
	displayNameOverrides: Record<string, string> = {};

	@state()
	filters: IFilter[] = [];

	@state()
	appliedFilters: IAppliedFilter[] = [];

	@state()
	isMobile: boolean = false;

	@state()
	dialogOpen: boolean = false;

	@state()
	dialogItemIndex: number | null = null;

	@state()
	allFiltersActive: boolean = false;

	@state()
	filtersLoaded: boolean = false;

	@state()
	shouldShowNextDay: boolean = false;

	@state()
	openFilterName: string | null = null;

	private boundHandleClickOutside: (event: Event) => void;
	private boundHandleResize: () => void;
	private boundHandleFiltersUpdated: (event: any) => void;
	private boundHandleSecondaryColClickOutside: (event: Event) => void;
	private boundHandleEscapeKey: (event: KeyboardEvent) => void;
	private NEXT_DAY_DELIVERY_SELECTOR = 'input[name="productstocklevel"][value="Next Day Delivery"]';

	constructor() {
		super();

		this.boundHandleClickOutside = this.handleClickOutside.bind(this);
		this.boundHandleResize = this.handleResize.bind(this);
		this.boundHandleSecondaryColClickOutside = this.handleSecondaryColClickOutside.bind(this);
		this.boundHandleEscapeKey = this.handleEscapeKey.bind(this);
		this.boundHandleFiltersUpdated = (event: any) => {
			const updatedFilters = event.detail as IFiltersResponse;
			const filteredFilters = updatedFilters.Filters.filter(filter => this.filtersToShow.includes(filter.Name));
			// Sort filters to match the order specified in filtersToShow
			this.filters = filteredFilters.sort((a, b) => this.filtersToShow.indexOf(a.Name) - this.filtersToShow.indexOf(b.Name));
			if (this.openFilterName && !this.filters.some(filter => filter.Name === this.openFilterName)) {
				this.openFilterName = null;
				this.openIndex = null;
			}

			this.appliedFilters = updatedFilters.AppliedFilters.filter(appliedFilter => {
				return !this.appliedFiltersToHide.some(hide => appliedFilter.Name.includes(hide));
			});
			this.filtersLoaded = true;
			
			// Show next day toggle only when the filter exists and has at least one result
			const nextDayFilter = updatedFilters.Filters.find(filter => filter.Name === 'productstocklevel');
			const hasNextDayResults = nextDayFilter
				? Object.values(nextDayFilter.ValuesLookup || {}).some(option => option.Value === 'Next Day Delivery' && option.Hits > 0)
				: false;
			this.shouldShowNextDay = this.showNextDay && hasNextDayResults;
			
			// Sync next day delivery checkbox state from main filter if it exists
			if (this.shouldShowNextDay) {
				this.waitForNextDayDeliveryElementAndSync();
			}
			
			this.requestUpdate();
		};
	}

	private waitForNextDayDeliveryElementAndSync(maxRetries: number = 10, retryDelay: number = 50): void {
		let retryCount = 0;

		const attemptSync = () => {
			const mainFilterCheckbox = document.querySelector(this.NEXT_DAY_DELIVERY_SELECTOR) as HTMLInputElement;
			const quickFilterCheckbox = this.querySelector('#quick-filter-next-day-delivery') as HTMLInputElement;

			if (mainFilterCheckbox && quickFilterCheckbox) {
				this.syncNextDayDeliveryFromMainFilter();
				return;
			}

			retryCount++;
			if (retryCount < maxRetries) {
				setTimeout(attemptSync, retryDelay);
			}
		};

		// Use requestAnimationFrame to ensure DOM has been updated
		requestAnimationFrame(() => {
			attemptSync();
		});
	}


	private handleFilterSelect(option: IFilterValue, isDialog: boolean = false) {

		const mainFilterElement = document.getElementById(option.Id) as HTMLInputElement;
		if (mainFilterElement) {

			mainFilterElement.checked = !mainFilterElement.checked;

			// Track quick filter click event
			const filterName = this.filters.find(filter => filter.Values.some(filterValue => filterValue.Id === option.Id))?.Name || 'unknown';
			dataLayer.push({
				event: 'quick_filter_clicked',
				filter_name: filterName,
				filter_value: option.Value
			});

			// Immediately update the local state to keep UI in sync
			this.updateLocalFilterState(option.Id, mainFilterElement.checked);

			// Trigger the native change event on the main filter element
			const changeEvent = new Event('change', {
				bubbles: true,
				cancelable: true
			});
			mainFilterElement.dispatchEvent(changeEvent);

		} else {
			console.error('No matching main filter input found for ID:', option.Id);
			console.info('Available filter elements:', Array.from(document.querySelectorAll('input[type="checkbox"]')).map(element => ({ id: element.id, dataId: element.getAttribute('data-id') })));
		}

		if (isDialog) {
			this.closeDialog();
		}
	}

	private handleNextDayDeliveryToggle() {
		const quickFilterCheckbox = this.querySelector('#quick-filter-next-day-delivery') as HTMLInputElement;
		const mainFilterCheckbox = document.querySelector(this.NEXT_DAY_DELIVERY_SELECTOR) as HTMLInputElement;
		
		if (mainFilterCheckbox && quickFilterCheckbox && mainFilterCheckbox.checked !== quickFilterCheckbox.checked) {
			// Sync the main checkbox with quick filter
			mainFilterCheckbox.checked = quickFilterCheckbox.checked;
			
			// Trigger change event on main filter
			const changeEvent = new Event('change', {
				bubbles: true,
				cancelable: true
			});
			mainFilterCheckbox.dispatchEvent(changeEvent);
		}
	}

	private syncNextDayDeliveryFromMainFilter() {
		const quickFilterCheckbox = this.querySelector('#quick-filter-next-day-delivery') as HTMLInputElement;
		const mainFilterCheckbox = document.querySelector(this.NEXT_DAY_DELIVERY_SELECTOR) as HTMLInputElement;
		
		if (mainFilterCheckbox && quickFilterCheckbox) {
			// Sync quick filter checkbox state from main filter
			quickFilterCheckbox.checked = mainFilterCheckbox.checked;
		}
	}

	// Update local filter state immediately to prevent visual lag
	private updateLocalFilterState(optionId: string, isSelected: boolean) {
		this.filters = this.filters.map(filter => ({
			...filter,
			ValuesLookup: Object.fromEntries(
				Object.entries(filter.ValuesLookup).map(([key, filterValue]) => [
					key,
					filterValue.Id === optionId ? { ...filterValue, IsSelected: isSelected } : filterValue
				])
			)
		}));
		this.requestUpdate();
	}

	connectedCallback() {
		super.connectedCallback();
		document.addEventListener('click', this.boundHandleClickOutside);
		document.addEventListener('click', this.boundHandleSecondaryColClickOutside);
		document.addEventListener('keyup', this.boundHandleEscapeKey);
		this.handleResize();
		window.addEventListener('resize', this.boundHandleResize);
		document.addEventListener('filtersUpdated', this.boundHandleFiltersUpdated);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.filters = [];
		this.appliedFilters = [];
		document.removeEventListener('click', this.boundHandleClickOutside);
		document.removeEventListener('click', this.boundHandleSecondaryColClickOutside);
		document.removeEventListener('keyup', this.boundHandleEscapeKey);
		window.removeEventListener('resize', this.boundHandleResize);
		document.removeEventListener('filtersUpdated', this.boundHandleFiltersUpdated);
	}

	private handleResize() {
		this.isMobile = window.matchMedia('(max-width: 979px)').matches;
	}

	private handleClickOutside(event: Event) {
		const target = event.target as HTMLElement;

		if (this.openFilterName === null) {
			return;
		}

		const childOffset = this.showAllFiltersButton ? 2 : 1;
		const currentIndex = this.getVisibleFilters().findIndex(f => f.Name === this.openFilterName);
		const openItem = currentIndex !== -1
			? this.querySelector(`.c-quick-filters__item:nth-child(${currentIndex + childOffset})`)
			: null;
		const isClickInsideOpenItem = openItem?.contains(target);

		if (!isClickInsideOpenItem) {
			this.openFilterName = null;
			this.openIndex = null;
			this.closeDialog();
			this.requestUpdate();
		}
	}

	private handleSecondaryColClickOutside(event: Event) {
		if (!this.allFiltersActive) {
			return;
		}

		const target = event.target as HTMLElement;
		const secondaryCol = document.querySelector('.js-filter-slide-out');
		const allFiltersButton = this.querySelector('.c-quick-filters__all-filters-button');

		// Check if click is outside the secondary column and not on the all filters button
		const isClickInsideSecondaryCol = secondaryCol?.contains(target);
		const isClickOnAllFiltersButton = allFiltersButton?.contains(target);
		const isClickInsideTooltip = target.closest('.js-tooltip') !== null;
		const isClickOnCloseButton = target.closest('.js-filter-slide-close') !== null;

		if ((!isClickInsideSecondaryCol && !isClickOnAllFiltersButton && !isClickInsideTooltip) || isClickOnCloseButton) {
			this.closeAllFiltersPanel();
		}
	}

	private handleEscapeKey(event: KeyboardEvent) {
		if (!this.allFiltersActive) {
			return;
		}

		const key = Helpers.getKeypressed(event);
		if (key === 'Escape' || key === 'Esc' || key === 27) {
			this.closeAllFiltersPanel();
		}
	}

	private closeAllFiltersPanel() {
		this.allFiltersActive = false;
		const secondaryCol = document.querySelector('.js-filter-slide-out');

		if (secondaryCol) {
			secondaryCol.classList.remove('is-active');
			secondaryCol.classList.add('is-transitioning');
			document.body.classList.remove('all-filters-open');
			const handleTransitionEnd = () => {
				secondaryCol.classList.remove('is-transitioning');
				secondaryCol.removeEventListener('transitionend', handleTransitionEnd);
			};
			secondaryCol.addEventListener('transitionend', handleTransitionEnd);
		}

		this.requestUpdate();
	}

	private openIndex: number | null = null;

	private handleToggle(index: number, filterName: string) {
		if (this.isMobile) {
			this.openDialog(index);
		} else {
			const shouldClose = this.openFilterName === filterName;
			this.openFilterName = shouldClose ? null : filterName;
			this.openIndex = shouldClose ? null : index;
			this.requestUpdate();
		}
	}

	private openDialog(index: number) {
		this.dialogItemIndex = index;
		this.dialogOpen = true;
		this.openFilterName = null;
		this.openIndex = null; // Close any desktop dropdown
		this.requestUpdate();
	}

	private closeDialog() {
		this.dialogOpen = false;
		this.dialogItemIndex = null;
		this.requestUpdate();
	}

	private handleClear() {
		this.openFilterName = null;
		this.openIndex = null;
		this.requestUpdate();
		this.closeDialog();

		const appliedFilterNames = this.appliedFilters.map(appliedFilter => appliedFilter.Name);

		document.getElementById('clearAllFilters')?.click();
	}

	private handleRemoveAppliedFilter(id: string) {
		const removeButton = document.querySelector(`.js-applied-filter[data-facet-id="${id}"]`) as HTMLLinkElement;
		if (removeButton) {
			removeButton.click();
		} else {
			console.error('No remove button found for applied filter ID:', id);
		}
	}

	private handleAllFiltersToggle() {
		this.allFiltersActive = !this.allFiltersActive;
		const secondaryCol = document.querySelector('.js-filter-slide-out');
		if (secondaryCol) {
			if (this.allFiltersActive) {
				secondaryCol.classList.add('is-active');
				secondaryCol.classList.add('is-transitioning');
				document.body.classList.add('all-filters-open');
				const handleTransitionEnd = () => {
					secondaryCol.classList.remove('is-transitioning');
					if (Helpers.isUsingKeyboard()) {
						const focusableElements = secondaryCol.querySelectorAll<HTMLElement>('a, button, input, select, textarea');
						if (focusableElements.length > 0) {
							focusableElements[0].focus();
						}
					}
					secondaryCol.removeEventListener('transitionend', handleTransitionEnd);
				};
				secondaryCol.addEventListener('transitionend', handleTransitionEnd);
			} else {
				secondaryCol.classList.remove('is-active');
				secondaryCol.classList.add('is-transitioning');
				// Wait for CSS transition to complete before removing body class
				const handleTransitionEnd = () => {
					document.body.classList.remove('all-filters-open');
					document.body.classList.remove('is-transitioning');
					secondaryCol.removeEventListener('transitionend', handleTransitionEnd);
				};
				
				secondaryCol.addEventListener('transitionend', handleTransitionEnd);
				
				// Fallback timeout in case transitionend doesn't fire
				setTimeout(() => {
					document.body.classList.remove('all-filters-open');
					secondaryCol.removeEventListener('transitionend', handleTransitionEnd);
				}, 500);
			}
		}
		this.requestUpdate();
	}

	private getDisplayName(filter: IFilter): string {
		return this.displayNameOverrides[filter.Name] || filter.DisplayName;
	}

	private isFilterOpen(filterName: string, index: number): boolean {
		if (this.openFilterName !== null) {
			return this.openFilterName === filterName;
		}

		return this.openIndex === index;
	}

	private getSelectedCount(filter: IFilter): number {
		if (!filter.ValuesLookup) {
			return 0;
		}
		return Object.values(filter.ValuesLookup).filter(option => option.IsSelected).length;
	}

	private renderPlaceholderItem(filterName: string) {
		const displayName = this.displayNameOverrides[filterName] || filterName;
		return html`
			<div class="c-quick-filters__item c-quick-filters__item--placeholder">
				<button
					type="button"
					class="c-quick-filters__item-button"
					disabled
					aria-label="Loading ${displayName} filter"
				>
					<span class="c-quick-filters__icon-group">
						<span class="c-quick-filters__selected-option">${displayName}</span>
					</span>
				</button>
			</div>
		`;
	}

	private isRangeFilter(filter: IFilter): boolean {
		return filter.AllowCustomRanges === true;
	}

	private shouldShowQuickFilterItem(filter: IFilter): boolean {
		if (!filter.ValuesLookup) {
			return true;
		}

		return Object.values(filter.ValuesLookup).some(option => option.Hits > 0);
	}

	private getVisibleFilters(): IFilter[] {
		return this.filters.filter(filter => this.shouldShowQuickFilterItem(filter));
	}

	private getMinMaxValues(filter: IFilter): { minValue: string, maxValue: string, minId: string, maxId: string } {
		// Range filters should have min/max values stored
		// Check if the filter data has these properties from the backend
		return {
			minValue: (filter as any).MinValue || '',
			maxValue: (filter as any).MaxValue || '',
			minId: (filter as any).MinId || `min-${filter.Name}`,
			maxId: (filter as any).MaxId || `max-${filter.Name}`
		};
	}

	private handleRangeApply(filter: IFilter) {
		const minInput = this.querySelector(`#quick-filters-min-${filter.Name}`) as HTMLInputElement;
		const maxInput = this.querySelector(`#quick-filters-max-${filter.Name}`) as HTMLInputElement;
		
		if (!minInput || !maxInput) {
			return;
		}
		
		// Store values before any DOM manipulation
		const minValue = minInput.value.trim();
		const maxValue = maxInput.value.trim();
		
		// Validate inputs before applying
		if (!this.validateRangeInputs(minInput, maxInput)) {
			return;
		}
		
		// Check if at least one value is provided
		if (!minValue && !maxValue) {
			return;
		}
		
		const mainMinInput = document.querySelector(`input[name="min-${filter.Name}"]`) as HTMLInputElement;
		const mainMaxInput = document.querySelector(`input[name="max-${filter.Name}"]`) as HTMLInputElement;
		
		// Sync all inputs with the stored values
		if (mainMinInput) {
			const allMinInputsByName = document.querySelectorAll(`input[name="min-${filter.Name}"]`) as NodeListOf<HTMLInputElement>;
			allMinInputsByName.forEach((input) => {
				input.value = minValue;
			});
		}
		
		if (mainMaxInput) {
			const allMaxInputsByName = document.querySelectorAll(`input[name="max-${filter.Name}"]`) as NodeListOf<HTMLInputElement>;
			allMaxInputsByName.forEach((input) => {
				input.value = maxValue;
			});
		}
		
		// Trigger the apply button in the main filters
		const applyButton = document.querySelector(`.js-apply-range-filter[data-min-max-apply="apply-range-filter-${filter.Name}"]`) as HTMLButtonElement;
		if (applyButton) {
			applyButton.classList.remove('is-faded');
			applyButton.disabled = false;
			applyButton.click();
		} else {
			// Fallback: dispatch change events to trigger filter update
			if (mainMinInput && minValue) {
				mainMinInput.value = minValue;
				const changeEvent = new Event('change', { bubbles: true });
				mainMinInput.dispatchEvent(changeEvent);
			}
			
			if (mainMaxInput && maxValue) {
				mainMaxInput.value = maxValue;
				const changeEvent = new Event('change', { bubbles: true });
				mainMaxInput.dispatchEvent(changeEvent);
			}
		}
		
		this.requestUpdate();
	}

	private validateRangeInputs(minInput: HTMLInputElement | null, maxInput: HTMLInputElement | null): boolean {
		if (!minInput || !maxInput) {
			return true;
		}

		const minValue = parseFloat(minInput.value);
		const maxValue = parseFloat(maxInput.value);
		const hasMinValue = minInput.value.trim() !== '';
		const hasMaxValue = maxInput.value.trim() !== '';

		// Clear any previous validation errors
		const rangeWrapper = minInput.closest('.c-quick-filters__range-inputs') as HTMLElement;
		if (rangeWrapper) {
			rangeWrapper.classList.remove('range-validation-error');
			const errorMessage = rangeWrapper.parentElement?.querySelector('.range-validation-error-message');
			if (errorMessage) {
				errorMessage.remove();
			}
		}

		// Only validate if both values are provided
		if (hasMinValue && hasMaxValue && !isNaN(minValue) && !isNaN(maxValue) && minValue > maxValue) {
			if (rangeWrapper) {
				rangeWrapper.classList.add('range-validation-error');
				
				const errorDiv = document.createElement('div');
				errorDiv.className = 'range-validation-error-message';
				errorDiv.setAttribute('role', 'alert');
				errorDiv.setAttribute('aria-live', 'assertive');
				errorDiv.textContent = '*Minimum value cannot be greater than maximum value.';
				errorDiv.style.display = 'block';
				
				if (rangeWrapper.parentNode) {
					rangeWrapper.parentNode.insertBefore(errorDiv, rangeWrapper.nextSibling);
				}
			}
			return false;
		}

		return true;
	}

	private handleRangeInputChange(filter: IFilter) {
		const minInput = this.querySelector(`#quick-filters-min-${filter.Name}`) as HTMLInputElement;
		const maxInput = this.querySelector(`#quick-filters-max-${filter.Name}`) as HTMLInputElement;
		
		// Find the apply button within the same dropdown using the filter name
		const dropdown = this.querySelector(`#quick-filters-options-${this.filters.indexOf(filter)}`) as HTMLElement;
		const applyButton = dropdown?.querySelector('.c-quick-filters__range-apply') as HTMLButtonElement;
		
		if (!minInput || !maxInput || !applyButton) {
			return;
		}
		
		const hasMinValue = minInput.value.trim() !== '';
		const hasMaxValue = maxInput.value.trim() !== '';
		const isValid = this.validateRangeInputs(minInput, maxInput);
		
		const shouldDisable = !isValid || (!hasMinValue && !hasMaxValue);
		applyButton.disabled = shouldDisable;
		applyButton.classList.toggle('is-faded', shouldDisable);
	}

	render() {
		// Show placeholders if filters haven't loaded yet and we have filtersToShow defined
		const showPlaceholders = !this.filtersLoaded && this.filtersToShow.length > 0;
		const visibleFilters = this.getVisibleFilters();
		
		return html`
			<div class="c-quick-filters ${this.openFilterName !== null ? 'c-quick-filters--dropdown-open' : ''}">
				${this.showAllFiltersButton ? html`
					<div class="c-quick-filters__item" data-test="quick-filter-all-filters">
						<button
							type="button"
							data-test="quick-filter-all-filters"
							class="c-quick-filters__item-button c-quick-filters__all-filters-button ${this.allFiltersActive ? 'is-active' : ''}"
							@click="${this.handleAllFiltersToggle}"
							?disabled="${showPlaceholders}"
							aria-expanded="${this.allFiltersActive}"
							aria-controls="all-filters-panel"
						>
							<span class="c-quick-filters__icon-group">
								<span class="c-quick-filters__selected-option">All filters</span>
							</span>
						</button>
					</div>
				` : ''}
				${showPlaceholders 
					? this.filtersToShow.map(filterName => this.renderPlaceholderItem(filterName))
					: visibleFilters.map((filter, index) => {
						const isRange = this.isRangeFilter(filter);
						const rangeValues = isRange ? this.getMinMaxValues(filter) : null;
						const selectedCount = this.getSelectedCount(filter);
						const isOpen = this.isFilterOpen(filter.Name, index);
						
						return html`
					<div class="c-quick-filters__item" data-test="quick-filter-${filter.Name}" data-filter-name="${this.getDisplayName(filter)}">
						<button
							type="button"
							data-test="quick-filter-dropdown-${filter.Name}"
							class="c-quick-filters__item-button"
							aria-expanded="${isOpen}"
							aria-controls="quick-filters-options-${index}"
							aria-haspopup="true"
							@click="${() => this.handleToggle(index, filter.Name)}"
						>
							<span class="c-quick-filters__icon-group">
								<span class="c-quick-filters__selected-option" data-test="quick-filter-selected-count">${this.getDisplayName(filter)}${selectedCount > 0 ? html`<span class="c-quick-filters__number">${selectedCount}</span>` : ''}</span>
							</span>

						</button>
						${!this.isMobile ? html`
							<div
								class="c-quick-filters__item-options ${isRange ? 'c-quick-filters__item-options--range' : ''}"
								id="quick-filters-options-${index}"
								aria-hidden="${!isOpen}"
								style="display: ${isOpen ? 'block' : 'none'};"
								data-test="quick-filters-options"
							>
								<div class="c-quick-filters__item-options-inner">
									${isRange ? html`
										<div class="rangeFilter c-quick-filters__range-inputs">
											<div class="rangeMin-wrapper c-quick-filters__range-input-wrapper">
												<input
													type="number"
													id="quick-filters-min-${filter.Name}"
													class="rangeMin c-quick-filters__range-input"
													placeholder=" "
													.value="${live(rangeValues.minValue)}"
													@input="${() => this.handleRangeInputChange(filter)}"
													@keydown="${(e: KeyboardEvent) => {
														if (e.key === 'Enter') {
															e.preventDefault();
															this.handleRangeApply(filter);
														}
													}}"
													data-test="quick-filters-min-${filter.Name}"
												/>
												<label for="quick-filters-min-${filter.Name}" class="rangeMin-label">Min</label>
											</div>
											<div class="rangeMax-wrapper c-quick-filters__range-input-wrapper">
												<input
													type="number"
													id="quick-filters-max-${filter.Name}"
													class="rangeMax c-quick-filters__range-input"
													placeholder=" "
													.value="${live(rangeValues.maxValue)}"
													@input="${() => this.handleRangeInputChange(filter)}"
													@keydown="${(e: KeyboardEvent) => {
														if (e.key === 'Enter') {
															e.preventDefault();
															this.handleRangeApply(filter);
														}
													}}"
													data-test="quick-filters-max-${filter.Name}">
												<label for="quick-filters-max-${filter.Name}" class="rangeMax-label">Max</label>
											</div>
											<button
												type="button"
												class="c-quick-filters__range-apply c-button c-button--small is-faded"
												disabled
												@click="${() => this.handleRangeApply(filter)}"
												data-test="quick-filters-apply-${filter.Name}"
											>
												Apply
											</button>
										</div>
									` : nothing}
									${Object.values(filter.ValuesLookup).map(filterOption =>
											html`
											<label class="c-quick-filters__item-option${filterOption.IsSelected ? ' c-quick-filters__item-option--selected' : ''}${((filterOption as any).isZeroResult && !filterOption.IsSelected) ? ' js-filter-option-zero-results' : ''} c-quick-filters__item-option--${filterOption.Key.toLowerCase()}" for="quick-filters-option-${filterOption.Id}" data-test="quick-filter-option">
											<span class="c-form__checkbox-container c-form__checkbox-container--small c-quick-filters__checkbox">
												<input
														type="checkbox"
														data-test="quick-filter-option-${filterOption.Text.replace(/ /g, '')}"
														data-filter-option-hit-count="${filterOption.Hits}"
														value="${filterOption.Text}"
														class="c-form__checkbox"
														id="quick-filters-option-${filterOption.Id}"
													.checked="${live(filterOption.IsSelected)}"
													?disabled="${(filterOption as any).isZeroResult && !filterOption.IsSelected}"
													@click="${() => this.handleFilterSelect(filterOption)}"
													/>
													<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" focusable="false" class="c-form__checkbox-icon">
													<path d="M27 4l-15 15-7-7-5 5 12 12 20-20z" fill="currentColor"></path>
												</svg>
												</span>
												${filterOption.Key === 'Colour' ?
											html`<img src="/images/Swatches/${filterOption.Value}.png?height=21&width=21&mode=crop&scale=both&quality=80" loading="lazy" alt="${filterOption.Text} color swatch" height="21" width="21" data-test="quick-filter-colour-swatch"/>`
													: ''
												}
												<span class="c-quick-filters__option-text">${filterOption.Text} <span class="c-quick-filters__option-hits">(${filterOption.Hits})</span></span>
											</label>
										`
							)}
								</div>
							</div>
						` : ''}
					</div>
				`})}
				${this.showNextDay && this.shouldShowNextDay ? html`
					<div class="c-quick-filters__item" data-test="quick-filter-next-day-delivery">
						<label class="c-toggle c-quick-filters__next-day-delivery" for="quick-filter-next-day-delivery">
							<span class="c-toggle__label" aria-label="Next Day delivery">Next Day</span>
							<input 
								type="checkbox" 
								name="productstocklevel" 
								id="quick-filter-next-day-delivery" 
								class="c-toggle__input"
								@change="${this.handleNextDayDeliveryToggle}"
								data-test="quick-filter-next-day-delivery-checkbox"
							>
							<span class="c-toggle__slider"></span>
						</label>
					</div>
				` : nothing}
				${this.getSlot('filters-end') ? this.getSlot('filters-end') : ''}
			</div>

			${this.isMobile && this.dialogOpen && this.dialogItemIndex !== null && visibleFilters[this.dialogItemIndex] ? html`
				<dialog
					open
					class="c-quick-filters__dialog c-dialog c-dialog--quick-filters"
					@click="${(e: Event) => {
					if (e.target === e.currentTarget) {
						this.closeDialog();
					}
				}}"
				>
					<div class="c-dialog__content">
					<div class="c-quick-filters__dialog-content">
						<div class="c-quick-filters__dialog-header">
							<h3 class="c-quick-filters__dialog-title">${this.getDisplayName(visibleFilters[this.dialogItemIndex])}</h3>
							<button
								type="button"
								class="c-quick-filters__dialog-close c-dialog__close"
								@click="${this.closeDialog}"
							><span class="u-visuallyhidden">Close overlay</span></button>
						</div>
						<div class="c-quick-filters__dialog-options">
							${Object.values(visibleFilters[this.dialogItemIndex].ValuesLookup).map(filterOption =>
					html`
								<label class="c-quick-filters__item-option${filterOption.IsSelected ? ' c-quick-filters__item-option--selected' : ''}${((filterOption as any).isZeroResult && !filterOption.IsSelected) ? ' js-filter-option-zero-results' : ''}" for="quick-filters-dialog-option-${filterOption.Id}" data-test="quick-filter-option">
								<span class="c-form__checkbox-container c-form__checkbox-container--small c-quick-filters__checkbox">
								<input
										type="checkbox"
										class="c-quick-filters__checkbox"
										id="quick-filters-dialog-option-${filterOption.Id}"
										data-filter-option-hit-count="${filterOption.Hits}"
										value="${filterOption.Text}"
										.checked="${live(filterOption.IsSelected)}"
										?disabled="${(filterOption as any).isZeroResult && !filterOption.IsSelected}"
										@click="${(e: Event) => {
											this.handleFilterSelect(filterOption, true);
										}}"
									/>
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" focusable="false" class="c-form__checkbox-icon">
										<path d="M27 4l-15 15-7-7-5 5 12 12 20-20z" fill="currentColor"></path>
									</svg>
									</span>
									${filterOption.Key === 'Colour' ?
									html`<img src="/images/Swatches/${filterOption.Value}.png?height=21&width=21&mode=crop&scale=both&quality=80" loading="lazy" alt="${filterOption.Text} color swatch" height="21" width="21" height="21" width="21"/>`
							: ''
						}
									<span class="c-quick-filters__option-text">${filterOption.Text} <span class="c-quick-filters__option-hits">(${filterOption.Hits})</span></span>
								</label>
								`
				)}
						</div>
					</div>
					</div>
				</dialog>
			` : ''}

			${this.appliedFilters.length > 0 ? html`
				<div class="c-quick-filters__applied-filters">
					${this.appliedFilters.map(appliedFilter => html`
						<a href="${appliedFilter.UrlWithoutFacet}" data-test="quick-filters-applied-filter" class="c-quick-filters__applied-filter" data-facet-id="${appliedFilter.Id}" aria-label="Remove ${appliedFilter.Name} filter" @click="${(e: Event) => {
						e.preventDefault();
						this.handleRemoveAppliedFilter(appliedFilter.Id);
					}}">
							<span class="c-quick-filters__applied-filter-text">${appliedFilter.Name} <span class="c-quick-filters__applied-filter-remove">&times;</span></span>
						</a>
					`)}
					${this.showClearAll && !showPlaceholders ? html`
					<div class="c-quick-filters__actions">
						<button
							type="button"
							data-test="quick-filters-clear-all"
							class="c-quick-filters__clear-button u-link-button u-link"
							@click="${this.handleClear}"
						>
							Clear all
						</button>
					</div>` : nothing}
				</div>
			` : ''}

		`;
	}
}
